import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  buildFacilityDiff,
  getFacilityForEdit,
  updateFacility,
} from '../api/editFacilityApiService';

import { FACILITY_LABELS } from '../constants/facilityLabels';
import { FACILITY_EDIT_ERROR_MESSAGES } from '../errors/errorMessages';
import {
  EDIT_FACILITY_MANDATORY_FIELDS,
  INITIAL_EDIT_FACILITY_FORM_DATA,
  type EditFacilityFormData,
  type EditFacilityFormErrors,
} from '../types/editFacilityType';
import {
  formatNip,
  validateNip,
  validateFacilityPhone as validatePhone,
  validateZip,
} from '../utils/validation';

const REQUIRED_MESSAGE = 'To pole jest wymagane';

/**
 * Business logic hook for editing an existing facility.
 *
 * Fetches facility data on mount to pre-populate the form fields and stores the
 * committed baseline in a ref. On submit, only changed fields are sent (diff-based
 * PATCH), so optional fields like addressLine2 can be cleared by sending "".
 *
 * @param facilityId - UUID of the facility to edit (from URL param)
 */
export function useEditFacility(facilityId: string) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<EditFacilityFormData>(
    INITIAL_EDIT_FACILITY_FORM_DATA
  );
  const [facilitySequentialId, setFacilitySequentialId] = useState<
    string | null
  >(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [facilityStatus, setFacilityStatus] = useState<
    'active' | 'inactive' | 'cascaded' | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadError, setIsLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [errors, setErrors] = useState<EditFacilityFormErrors>({});
  const [snackbarError, setSnackbarError] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [initialEmployeeCount, setInitialEmployeeCount] = useState(0);
  const [initialEquipmentCount, setInitialEquipmentCount] = useState(0);

  // Tracks the last committed (saved) state so we can build a minimal diff on submit
  const committedFormData = useRef<EditFacilityFormData>(
    INITIAL_EDIT_FACILITY_FORM_DATA
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    void getFacilityForEdit(facilityId).then((facilityResult) => {
      if (cancelled) {
        return;
      }

      if (facilityResult.success && facilityResult.fields) {
        const fields = {
          ...facilityResult.fields,
          nip: formatNip(facilityResult.fields.nip),
        };
        setFormData(fields);
        committedFormData.current = fields;
        setFacilitySequentialId(facilityResult.facilitySequentialId ?? null);
        setClientId(facilityResult.clientId ?? null);
        setInitialEmployeeCount(facilityResult.employeeCount ?? 0);
        setInitialEquipmentCount(facilityResult.equipmentCount ?? 0);
        setFacilityStatus(facilityResult.facilityStatus ?? null);
      } else if (facilityResult.isAccessDenied) {
        setIsAccessDenied(true);
      } else {
        setIsLoadError(true);
      }

      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const handleFieldChange = useCallback(
    (field: keyof EditFacilityFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field as keyof EditFacilityFormErrors];
        return updated;
      });
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Client-side validation — mandatory fields non-empty; NIP, phone, zip also format-checked
      const newErrors: EditFacilityFormErrors = {};
      for (const field of EDIT_FACILITY_MANDATORY_FIELDS) {
        if (field === 'nip' || field === 'phoneNumber') {
          continue;
        } // validated separately below
        if (!formData[field].trim()) {
          (newErrors as Record<string, string>)[field] = REQUIRED_MESSAGE;
        }
      }
      const nipError = validateNip(formData.nip);
      if (nipError) {
        newErrors.nip = nipError;
      }
      const phoneError = validatePhone(formData.phoneNumber);
      if (phoneError) {
        newErrors.phoneNumber = phoneError;
      }
      const zipError = validateZip(formData.zipCode);
      if (zipError) {
        newErrors.zipCode = zipError;
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Build diff — skip API call entirely if nothing changed. Still show the
      // success message: the user may have only edited equipment/employees
      // (saved via their own submit calls) and clicked Zapisz to finish.
      const diff = buildFacilityDiff(formData, committedFormData.current);
      if (Object.keys(diff).length === 0) {
        navigate('/Placówki', {
          state: { successMessage: FACILITY_LABELS.EDIT_FACILITY_SUCCESS },
        });
        return;
      }

      setIsSubmitting(true);
      let navigating = false;
      try {
        const result = await updateFacility(facilityId, diff);
        if (result.success) {
          navigating = true;
          navigate('/Placówki', {
            state: { successMessage: FACILITY_LABELS.EDIT_FACILITY_SUCCESS },
          });
        } else if (result.isAccessDenied) {
          setSnackbarError(
            result.error ?? FACILITY_EDIT_ERROR_MESSAGES.ACCESS_DENIED
          );
          setIsSnackbarOpen(true);
        } else {
          setSnackbarError(
            result.error ?? FACILITY_EDIT_ERROR_MESSAGES.DEFAULT_ERROR
          );
          setIsSnackbarOpen(true);
        }
      } finally {
        if (!navigating) {
          setIsSubmitting(false);
        }
      }
    },
    [facilityId, formData, navigate]
  );

  const hasChanges =
    Object.keys(buildFacilityDiff(formData, committedFormData.current)).length >
    0;

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate(-1);
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(-1);
  }, [navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  const handleCloseSuccessSnackbar = useCallback(() => {
    setIsSuccessSnackbarOpen(false);
  }, []);

  return {
    formData,
    facilitySequentialId,
    clientId,
    facilityStatus,
    isLoading,
    isLoadError,
    isSubmitting,
    isAccessDenied,
    errors,
    snackbarError,
    isSnackbarOpen,
    isSuccessSnackbarOpen,
    isCancelModalOpen,
    hasChanges,
    initialEmployeeCount,
    initialEquipmentCount,
    handleFieldChange,
    handleSubmit,
    handleCancel,
    handleConfirmCancel,
    handleCloseCancelModal,
    handleCloseSnackbar,
    handleCloseSuccessSnackbar,
  };
}
